// =============================================================================
// domain/controllableLoss.ts — what indiscipline actually costs.
// -----------------------------------------------------------------------------
// A losing trade that carries evidence of preventable behaviour: a revenge, FOMO
// or overtraded flag, a stop that was not honoured, a mistake tag the trader has
// marked as controllable, or the manual override. Winners never qualify — a
// sloppy win is a different conversation.
//
// From that one set: what the book would have made without them, where the money
// goes, which days cost most, when in the day it happens, whether one mistake
// snowballs, and whether clean days really are better.
//
// ── 🚨 THE OLD CLIENT MANUFACTURES ITS OWN CONCLUSION ──
//
// Its clean-vs-dirty line reads:
//
//     A clean day is worth ${fmtPlainUnit(Math.abs(cleanAvg - dirtyAvg))} more
//     on average than a day you book a controllable loss — the case for
//     "one mistake and I'm done."
//
// `Math.abs` makes that sentence UNCONDITIONAL. A trader whose clean days are
// worse reads the same claim, with the size of the gap as evidence FOR the rule
// the gap actually argues against. It is not a dropped caveat; it is an inverted
// conclusion, printed with a number that makes it sound measured. PD-22.
//
// Here the difference is signed and the sentence follows it.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// =============================================================================

export interface ClTrade {
  /** The EFFECTIVE date — resolved by the caller. */
  date: string | null;
  timeIn: string | null;
  netPnl: number | null;
  booleans: Record<string, unknown> | null | undefined;
  mistakeTags: readonly string[] | null | undefined;
  /** For "did you give back a real winner". */
  mfePoints: number | null;
  riskPoints: number | null;
  /** ⚠ TOOLTIP ONLY — nothing here is scoped or scaled by instrument. */
  contract?: string | null;
}

/**
 * Whether a mistake tag counts as controllable.
 *
 * ⚠ DEFAULTS TO TRUE for a tag nobody has configured, which is the old client's
 * choice and the right one: a tag the trader invented is a mistake they thought
 * worth naming. The setting exists to EXCLUDE, not to opt in.
 */
export function isTagControllable(
  tag: string, config: Record<string, boolean> | null | undefined,
): boolean {
  if (!tag) return false;
  const cfg = config ?? {};
  return Object.prototype.hasOwnProperty.call(cfg, tag) ? cfg[tag] !== false : true;
}

/** Human-readable reasons this trade is controllable. Empty means auto found none. */
export function controllableReasons(
  t: ClTrade, config?: Record<string, boolean> | null,
): string[] {
  const out: string[] = [];
  const b = t.booleans ?? {};
  if (b['revengeTrade'] === true) out.push('revenge trade');
  if (b['fomoTrade'] === true) out.push('FOMO trade');
  if (b['overtraded'] === true) out.push('overtraded');
  // ⚠ A stop that was not honoured DEEPENED the loss, so it belongs here even
  // though it is not a decision to enter. The old client added it deliberately.
  if (b['stopNotHonored'] === true) out.push('stop not honored');
  for (const tag of t.mistakeTags ?? []) {
    if (isTagControllable(tag, config)) out.push(tag);
  }
  return out;
}

/**
 * ⚠ LOSERS ONLY, and the whole loss is attributed.
 *
 * Partial amounts were removed from the old client on purpose: if a mistake was
 * tagged on a loss, the loss owes to the mistake. Splitting it invites arguing
 * about the split rather than about the behaviour.
 */
export function isControllableLoss(
  t: ClTrade, config?: Record<string, boolean> | null,
): boolean {
  if ((Number(t.netPnl) || 0) >= 0) return false;
  if (t.booleans?.['controllableLoss'] === true) return true;
  return controllableReasons(t, config).length > 0;
}

export const clAmount = (t: ClTrade): number => Math.abs(Number(t.netPnl) || 0);

/**
 * One preventable loss on the chart, oldest first.
 *
 * ⚠ `source` says WHY it counts, not what it cost. A loss is `auto` when the
 * trade carries a detected reason, and `manual` when the trader ticked the box
 * with nothing else on it. The WHOLE loss is attributed either way — see
 * `isControllableLoss` — so the two are a label on one bar, never a split of it.
 */
export interface ClPoint {
  date: string | null;
  timeIn: string | null;
  contract: string | null;
  /** Positive magnitude. */
  amount: number;
  /** The running cost, oldest first. ⚠ The LAST one IS `ControllableLoss.total`. */
  cumulative: number;
  source: 'auto' | 'manual';
  reasons: string[];
}

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export interface LeakRow {
  reason: string;
  /** Trades carrying this reason. ⚠ A trade with two reasons is in both rows. */
  trades: number;
  total: number;
  per: number;
}

export interface WorstDay {
  date: string;
  total: number;
  reasons: string[];
}

export interface CleanVsDirty {
  cleanDays: number;
  dirtyDays: number;
  cleanAvg: number;
  dirtyAvg: number;
  cleanGreenPct: number;
  dirtyGreenPct: number;
  /**
   * ⚠ SIGNED: clean minus dirty. Positive means clean days really are better.
   * The old client takes the absolute value and always says "more". PD-22.
   */
  difference: number;
  /** Both sides need a sample before the comparison means anything. */
  comparable: boolean;
}

export interface Cascade {
  /** Days with a controllable loss AND more trading after it. */
  days: number;
  /** Of those, how many finished net red after the first mistake. */
  spiralled: number;
  spiralledPct: number | null;
  /** Average P&L of everything traded after the first controllable loss. */
  avgAfter: number | null;
}

export interface ControllableLoss {
  /** Controllable losing trades. */
  count: number;
  /** Their combined magnitude, positive. */
  total: number;
  /** The book's actual net. */
  net: number;
  /** ⚠ What it would have been WITHOUT them — net + total. */
  aGame: number;
  /** Their share of ALL losing dollars, 0–100. */
  shareOfLosses: number | null;
  leaks: LeakRow[];
  worstDays: WorstDay[];
  /** By trade number within its day, 1-indexed; `4` means fourth-or-later. */
  byTradeNumber: { nth: number; count: number }[];
  byHour: { hour: number; count: number }[];
  /** ⚠ NULL below three measurable trades — a share of two is not a share. */
  gaveBackWinnerPct: number | null;
  gaveBackSample: number;
  cleanVsDirty: CleanVsDirty;
  cascade: Cascade;
  /** Weekly totals, oldest first — the "are you cleaning it up" trend. */
  weekly: { weekStart: string; total: number }[];
  /**
   * Every controllable loss in date order, carrying the running cost.
   *
   * ⚠ `series.length === count` and the last `cumulative === total`, BY
   * CONSTRUCTION rather than by agreement — see the note in `controllableLoss`.
   */
  series: ClPoint[];
}

/** Trades past this are lumped into one "fourth or later" bucket. */
export const NTH_CAP = 4;
/** A share of fewer than this is not a share. */
export const GAVE_BACK_MIN = 3;
/** Below this on either side, clean-vs-dirty is not a comparison. */
export const CVD_MIN_DAYS = 2;
/** Favourable excursion of at least this many units of risk to count as a winner. */
export const REAL_WINNER_R = 1;

export function controllableLoss(
  trades: readonly ClTrade[],
  config?: Record<string, boolean> | null,
  weekStartOf?: (iso: string) => string | null,
): ControllableLoss {
  const cl = trades.filter((t) => isControllableLoss(t, config));

  // ── the series, and the headline that comes OUT of it ─────────────────────
  //
  // 🚨 `total` IS THE LAST CUMULATIVE. It is not a second sum over the same
  // trades, and that is the whole point: the chart draws this series while the
  // card prints `total` beside it, so they are one number appearing twice on one
  // screen. Floating-point addition is not associative — the same losses added
  // in filter order and in date order can differ in the last bits — so "the
  // chart ends where the headline says" is true here by construction, not by a
  // rounding coincidence.
  //
  // ▶ THIS IS THE THIRD ONE IN A WEEK. #88 was give-back, computed once on the
  // Day Report and again on Excursion; #89 was the account balance, computed
  // once on the Dashboard and again on Analytics. Both drifted. Neither had a
  // test asserting the two surfaces agreed, which is why nobody noticed.
  //
  // ⚠ SORTED BY EFFECTIVE DATE, THEN ENTRY TIME. The date is resolved by the
  // caller (PD-17), so an evening trade belongs to its session day here as
  // everywhere else. A trade with no date sorts first rather than being dropped:
  // dropping it would take its dollars out of the chart while leaving them in
  // the headline, which is the exact disagreement this block exists to prevent.
  let running = 0;
  const series: ClPoint[] = [...cl]
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')
      || (a.timeIn ?? '').localeCompare(b.timeIn ?? ''))
    .map((t) => {
      const reasons = controllableReasons(t, config);
      const amount = clAmount(t);
      running += amount;
      return {
        date: t.date,
        timeIn: t.timeIn,
        contract: t.contract ?? null,
        amount,
        cumulative: running,
        source: reasons.length ? ('auto' as const) : ('manual' as const),
        reasons,
      };
    });
  const total = series.length ? (series[series.length - 1] as ClPoint).cumulative : 0;
  const net = trades.reduce((s, t) => s + (Number(t.netPnl) || 0), 0);
  const lossMag = trades.reduce(
    (s, t) => ((Number(t.netPnl) || 0) < 0 ? s + clAmount(t) : s), 0,
  );

  // ── where the money goes ──────────────────────────────────────────────────
  // ⚠ A trade with two reasons is counted under BOTH, so these can add to more
  // than the headline. That is the old client's choice and the honest one — the
  // question is "what does chasing cost me", not "how do I divide this loss".
  const byReason = new Map<string, { n: number; total: number }>();
  for (const t of cl) {
    const reasons = controllableReasons(t, config);
    for (const r of (reasons.length ? reasons : ['manual flag'])) {
      const e = byReason.get(r) ?? { n: 0, total: 0 };
      e.n += 1;
      e.total += clAmount(t);
      byReason.set(r, e);
    }
  }
  const leaks: LeakRow[] = [...byReason.entries()]
    .map(([reason, v]) => ({ reason, trades: v.n, total: v.total, per: v.total / v.n }))
    .sort((a, b) => b.total - a.total || a.reason.localeCompare(b.reason))
    .slice(0, 6);

  // ── worst days ────────────────────────────────────────────────────────────
  const dayCl = new Map<string, { total: number; reasons: Set<string> }>();
  for (const t of cl) {
    if (!t.date) continue;
    const e = dayCl.get(t.date) ?? { total: 0, reasons: new Set<string>() };
    e.total += clAmount(t);
    for (const r of controllableReasons(t, config)) e.reasons.add(r);
    dayCl.set(t.date, e);
  }
  const worstDays: WorstDay[] = [...dayCl.entries()]
    .map(([date, v]) => ({ date, total: v.total, reasons: [...v.reasons] }))
    .sort((a, b) => b.total - a.total || a.date.localeCompare(b.date))
    .slice(0, 3);

  // ── when it strikes ───────────────────────────────────────────────────────
  const byDay = new Map<string, ClTrade[]>();
  for (const t of trades) {
    if (!t.date) continue;
    const arr = byDay.get(t.date) ?? [];
    arr.push(t);
    byDay.set(t.date, arr);
  }
  for (const [, arr] of byDay) {
    arr.sort((a, b) => (a.timeIn ?? '').localeCompare(b.timeIn ?? ''));
  }

  const nth = new Map<number, number>();
  for (const [, arr] of byDay) {
    arr.forEach((t, i) => {
      if (!isControllableLoss(t, config)) return;
      const n = Math.min(NTH_CAP, i + 1);
      nth.set(n, (nth.get(n) ?? 0) + 1);
    });
  }
  const byTradeNumber = [...nth.entries()]
    .map(([n, count]) => ({ nth: n, count }))
    .sort((a, b) => a.nth - b.nth);

  const hours = new Map<number, number>();
  for (const t of cl) {
    const h = Number(String(t.timeIn ?? '').split(':')[0]);
    if (!Number.isInteger(h) || h < 0 || h > 23) continue;
    hours.set(h, (hours.get(h) ?? 0) + 1);
  }
  const byHour = [...hours.entries()]
    .map(([hour, count]) => ({ hour, count }))
    .sort((a, b) => a.hour - b.hour);

  // ── gave back a real winner ───────────────────────────────────────────────
  // ⚠ "REAL" means it reached a full unit of risk. Being up a fraction of your
  // risk is noise, not a missed opportunity — and a trade with no logged stop
  // cannot be measured in R at all, so it is out of the denominator.
  let gbEligible = 0; let gbYes = 0;
  for (const t of cl) {
    const mfe = t.mfePoints;
    const rp = Number(t.riskPoints) || 0;
    if (mfe == null || rp <= 0) continue;
    gbEligible += 1;
    if (Math.abs(Number(mfe)) / rp >= REAL_WINNER_R) gbYes += 1;
  }

  // ── clean vs dirty ────────────────────────────────────────────────────────
  const dayNet = new Map<string, number>();
  const dirty = new Set<string>();
  for (const t of trades) {
    if (!t.date) continue;
    dayNet.set(t.date, (dayNet.get(t.date) ?? 0) + (Number(t.netPnl) || 0));
    if (isControllableLoss(t, config)) dirty.add(t.date);
  }
  const cleanVals: number[] = []; const dirtyVals: number[] = [];
  for (const [d, v] of dayNet) (dirty.has(d) ? dirtyVals : cleanVals).push(v);
  const avg = (a: number[]): number => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
  const greenPct = (a: number[]): number => (
    a.length ? Math.round((100 * a.filter((v) => v > 0).length) / a.length) : 0
  );
  const cleanAvg = avg(cleanVals); const dirtyAvg = avg(dirtyVals);

  // ── the cascade ───────────────────────────────────────────────────────────
  let cascadeDays = 0; let spiralled = 0; const afterSums: number[] = [];
  for (const [, arr] of byDay) {
    const idx = arr.findIndex((t) => isControllableLoss(t, config));
    if (idx < 0 || idx === arr.length - 1) continue;   // none, or nothing traded after
    cascadeDays += 1;
    const after = arr.slice(idx + 1).reduce((s, t) => s + (Number(t.netPnl) || 0), 0);
    afterSums.push(after);
    if (after < 0) spiralled += 1;
  }

  // ── the trend ─────────────────────────────────────────────────────────────
  const weekly: { weekStart: string; total: number }[] = [];
  if (weekStartOf) {
    const byWeek = new Map<string, number>();
    for (const t of cl) {
      if (!t.date) continue;
      const w = weekStartOf(t.date);
      if (!w) continue;
      byWeek.set(w, (byWeek.get(w) ?? 0) + clAmount(t));
    }
    for (const [weekStart, v] of [...byWeek.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      weekly.push({ weekStart, total: v });
    }
  }

  return {
    count: cl.length,
    total,
    net,
    aGame: net + total,
    shareOfLosses: lossMag > 0 ? (100 * total) / lossMag : null,
    leaks,
    worstDays,
    byTradeNumber,
    byHour,
    gaveBackWinnerPct: gbEligible >= GAVE_BACK_MIN
      ? Math.round((100 * gbYes) / gbEligible) : null,
    gaveBackSample: gbEligible,
    cleanVsDirty: {
      cleanDays: cleanVals.length,
      dirtyDays: dirtyVals.length,
      cleanAvg, dirtyAvg,
      cleanGreenPct: greenPct(cleanVals),
      dirtyGreenPct: greenPct(dirtyVals),
      // 🚨 SIGNED. See the header — the old client wraps this in `Math.abs` and
      // always says "more", so a trader whose clean days are WORSE reads the
      // size of that gap as evidence for the rule it argues against.
      difference: cleanAvg - dirtyAvg,
      comparable: cleanVals.length >= CVD_MIN_DAYS && dirtyVals.length >= CVD_MIN_DAYS,
    },
    cascade: {
      days: cascadeDays,
      spiralled,
      spiralledPct: cascadeDays ? Math.round((100 * spiralled) / cascadeDays) : null,
      avgAfter: afterSums.length ? avg(afterSums) : null,
    },
    weekly,
    series,
  };
}

// ---------------------------------------------------------------------------
// Drill-through — the trades behind a row
// ---------------------------------------------------------------------------

/**
 * The controllable losses carrying one reason, or falling on one day.
 *
 * ── 🚨 WHY THIS IS HERE AND NOT A FILTER IN THE COMPONENT ──
 *
 * The card's rows are built by `controllableLoss` above, using
 * `isControllableLoss` and `controllableReasons`. A drill-through that
 * re-filtered the same trades in the UI would be a SECOND copy of that rule, and
 * the first time the two disagreed the user would click a row reading "13
 * trades" and get a table of eleven — with nothing on screen to say which count
 * was right.
 *
 * That failure has a name in this repo: it is what #88 found between the Day
 * Report and the Excursion tab, and what #89 found between the Dashboard and
 * Analytics. Both were one number computed in two places. So the row and the
 * drill come out of one function.
 *
 * ⚠ THE `'manual flag'` FALLBACK IS PART OF THE RULE, not a display nicety. A
 * trade the user ticked by hand has no auto-detected reason, and the leaks table
 * files it under that label — so a drill on that row has to match it the same
 * way or the row would open empty.
 */
export function controllableLossTrades<T extends ClTrade>(
  trades: readonly T[],
  by: { reason: string } | { date: string },
  config?: Record<string, boolean> | null,
): T[] {
  const cl = trades.filter((t) => isControllableLoss(t, config));
  if ('date' in by) return cl.filter((t) => t.date === by.date);
  return cl.filter((t) => {
    const reasons = controllableReasons(t, config);
    return (reasons.length ? reasons : ['manual flag']).includes(by.reason);
  });
}
